import { useUpdateGoal } from 'features/goals';
import { useCallback, useRef } from 'react';
import { useCreateTaskWithSubTasksMutation } from 'shared/hooks/queries/use-create-task-with-sub-tasks-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import {
  ActionEvents,
  taskTypeToTrackingTaskType,
} from 'shared/services/tracking';
import { Goal } from 'shared/types/goal';
import { ID, IDPrefixes } from 'shared/types/id';
import {
  goalInboxID,
  goalOtherID,
  lifeAreaOtherID,
} from 'shared/types/orderable-section';
import { NewSubTask, NewTask, Task } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';
import { createPrefixId } from 'shared/utils/prefix-id';

const excludedGoalIds = [goalInboxID, goalOtherID];

export type ExtraOptions = {
  sectionId?: string;
  goal?: Goal;
  tags?: ID[] | null;
  subTasks?: NewSubTask[];
};

export const useCreateTask = () => {
  const track = useTrackEvents();
  const createTaskRef = useRef<NewTask>();
  const subTasksRef = useRef<NewSubTask[]>([]);
  const {
    mutateAsync: createTaskWithSubTasks,
    error,
    isPending,
    isSuccess,
    reset,
  } = useCreateTaskWithSubTasksMutation();
  // todo: find a better solution for this (cannot use other features here)
  const { submit: updateGoal } = useUpdateGoal();
  const updateRelationships = useUpdateRelationshipsReferences();

  const submit = useCallback(
    async (
      task: NewTask,
      { goal, sectionId, tags, subTasks = [] }: ExtraOptions = {},
    ): Promise<Task> => {
      // filter out inbox ID
      task.goalId = excludedGoalIds.includes(task.goalId!) ? null : task.goalId;
      task.lifeAreaId =
        task.lifeAreaId === lifeAreaOtherID ? null : task.lifeAreaId;

      createTaskRef.current = task;
      subTasksRef.current = subTasks;
      const { id } = await createTaskWithSubTasks({ task, subTasks });

      // update the goal if the task is bound to a goal and not an actionable task
      if (task.goalId && task.action?.type !== TaskActionType.GoalTimeslot) {
        // if a section is provided, update the section
        const sections =
          sectionId && goal?.sections?.find(({ id }) => id === sectionId)
            ? structuredClone(goal?.sections)
            : undefined;
        const sectionToUpdate = sections?.find(({ id }) => id === sectionId);
        sectionToUpdate?.tasks?.push(id);

        updateGoal({ id: task.goalId, taskCount: 1, sections });
      }

      if (tags?.length) {
        updateRelationships(
          tags.map((tagId) => ({
            id: createPrefixId(tagId, IDPrefixes.Tag),
            add: [createPrefixId(id, IDPrefixes.Task)],
          })),
        );
      }

      track(ActionEvents.TaskCreate, {
        type: taskTypeToTrackingTaskType[task.type],
        deadline_added: !!task.endStrategy?.deadline,
        linked_goal: !!task.goalId,
      });

      return {
        ...task,
        id,
      } as Task;
    },
    [createTaskWithSubTasks, track, updateGoal, updateRelationships],
  );

  const retry = useCallback(async () => {
    const task = createTaskRef.current;
    const subTasks = subTasksRef.current;

    if (task) {
      const { id } = await createTaskWithSubTasks({ task, subTasks });

      track(ActionEvents.TaskCreate, {
        type: taskTypeToTrackingTaskType[task.type],
        deadline_added: !!task.endStrategy?.deadline,
        linked_goal: !!task.goalId,
      });

      return {
        ...task,
        id,
      } as Task;
    }
  }, [createTaskWithSubTasks, track]);

  const resetAll = useCallback(() => {
    reset();
    createTaskRef.current = undefined;
  }, [reset]);

  return {
    submit,
    retry,
    reset: resetAll,
    error,
    isLoading: isPending,
    isSuccess,
  };
};
